import {
  Achievement,
  ReferralBonus,
  GameOutcome,
  PlayerPosition,
  MatchEventType,
  BonusScoreType,
  MatchPeriod,
  ContestGoal,
  MatchStatus,
  CreateGameResponse,
  PlayerStatus
} from 'protos'

const error = (token: string) =>
  new Error(`Missing translation token for ${token}.`)

export const AchievementTypeToken = (type: Achievement.Type) => {
  switch (type) {
    case Achievement.Type.AT_GAMES_PLAYED: return 'achievements_type_gamesplayed'
    case Achievement.Type.AT_GAMES_WON: return 'achievements_type_gameswon'
    case Achievement.Type.AT_CONTEST_WINNINGS: return 'achievements_type_contestwinnings'
    case Achievement.Type.AT_CONTEST_WINS: return 'achievements_type_contestwins'
    case Achievement.Type.AT_MATCH_SCORE: return 'achievements_type_matchscore'
    case Achievement.Type.AT_BALANCE: return 'achievements_type_balance'
    case Achievement.Type.AT_PLAYER_SCORE: return 'achievements_type_playerscore'
    case Achievement.Type.AT_LIVE_SUBS: return 'achievements_type_livesubs'
    case Achievement.Type.AT_FRIENDS: return 'achievements_type_friends'
    case Achievement.Type.AT_FRIEND_REFERRALS: return 'achievements_type_friendreferrals'
    case Achievement.Type.AT_FRIEND_GAMES: return 'achievements_type_friendgames'
    case Achievement.Type.AT_BUNDESLIGA_GAMES: return 'achievements_type_bundesligagames'
    case Achievement.Type.AT_BPL_GAMES: return 'achievements_type_britishpremierleaguegames'
    case Achievement.Type.AT_LALIGA_GAMES: return 'achievements_type_laligagames'
    case Achievement.Type.AT_SERIEA_GAMES: return 'achievements_type_serieagames'
    case Achievement.Type.AT_LIGUE1_GAMES: return 'achievements_type_ligue1games'
    case Achievement.Type.AT_ELCL_GAMES: return 'achievements_type_elclgames'
    case Achievement.Type.AT_NATIONAL_GAMES: return 'achievements_type_nationalgames'
    case Achievement.Type.AT_DAILY_BONUS_STREAK: return 'achievements_type_dailybonusstreak'
    case Achievement.Type.AT_WIN_STREAK: return 'achievements_type_winstreak'
    default: throw error(`Achievement.Type.${Achievement.Type[type]}`)
  }
}

export const WonReferralBonusTypeToken = (type: ReferralBonus.Type) => {
  switch (type) {
    case ReferralBonus.Type.RBT_REGISTRATION: return 'referrals_bonus_registration_title'
    case ReferralBonus.Type.RBT_PURCHASE: return 'referrals_bonus_purchase_title'
    case ReferralBonus.Type.RBT_MATCHES_10: return 'referrals_bonus_matches10_title'
    case ReferralBonus.Type.RBT_MATCHES_100: return 'referrals_bonus_matches100_title'
    default: throw error(`ReferralBonus.Type.${ReferralBonus.Type[type]}`)
  }
}

export const PossibleReferralBonusTypeToken = (type: ReferralBonus.Type) => {
  switch (type) {
    case ReferralBonus.Type.RBT_REGISTRATION: return 'referrals_bonus_registration_help_title'
    case ReferralBonus.Type.RBT_PURCHASE: return 'referrals_bonus_purchase_help_title'
    case ReferralBonus.Type.RBT_MATCHES_10: return 'referrals_bonus_matches10_help_title'
    case ReferralBonus.Type.RBT_MATCHES_100: return 'referrals_bonus_matches100_help_title'
    default: throw error(`ReferralBonus.Type.${ReferralBonus.Type[type]}`)
  }
}

export const GameOutcomeToken = (outcome: GameOutcome) => {
  switch (outcome) {
    case GameOutcome.GO_CANCELLED: return 'game_outcome_cancelled'
    case GameOutcome.GO_LOST: return 'game_outcome_lost'
    case GameOutcome.GO_REFUNDED: return 'game_outcome_refunded'
    case GameOutcome.GO_SOLO: return 'game_outcome_solo'
    case GameOutcome.GO_WON: return 'game_outcome_won'
    default: throw error(`GameOutcome.${GameOutcome[outcome]}`)
  }
}

export const PlayerPositionNameToken = (position: PlayerPosition) => {
  switch (position) {
    case PlayerPosition.PP_ATTACKER: return 'position_attacker'
    case PlayerPosition.PP_DEFENDER: return 'position_defender'
    case PlayerPosition.PP_GOALKEEPER: return 'position_goalkeeper'
    case PlayerPosition.PP_MIDFIELDER: return 'position_midfielder'
    default: throw error(`PlayerPosition.${PlayerPosition[position]}`)
  }
}

export const PlayerPositionCodeToken = (position: PlayerPosition) => {
  switch (position) {
    case PlayerPosition.PP_ATTACKER: return 'position_code_attacker'
    case PlayerPosition.PP_DEFENDER: return 'position_code_defender'
    case PlayerPosition.PP_GOALKEEPER: return 'position_code_goalkeeper'
    case PlayerPosition.PP_MIDFIELDER: return 'position_code_midfielder'
    default: throw error(`PlayerPosition.${PlayerPosition[position]}`)
  }
}

export const MatchEventToken = (type: MatchEventType) => {
  switch (type) {
    case MatchEventType.ET_ASSIST: return 'matcheventtype_as'
    case MatchEventType.ET_CORNER: return 'matcheventtype_c'
    case MatchEventType.ET_GOAL: return 'matcheventtype_g'
    case MatchEventType.ET_OWN_GOAL: return 'matcheventtype_og'
    case MatchEventType.ET_PENALTY_CONCEDED: return 'matcheventtype_pc'
    case MatchEventType.ET_PENALTY_GOAL: return 'matcheventtype_pg'
    case MatchEventType.ET_PENALTY_MISSED: return 'matcheventtype_pm'
    case MatchEventType.ET_PENALTY_SAVED: return 'matcheventtype_ps'
    case MatchEventType.ET_PENALTY_SHOOTOUT_GOAL: return 'matcheventtype_psg'
    case MatchEventType.ET_PENALTY_SHOOTOUT_MISSED: return 'matcheventtype_psm'
    case MatchEventType.ET_RED_CARD: return 'matcheventtype_rc'
    case MatchEventType.ET_SAVE: return 'matcheventtype_s'
    case MatchEventType.ET_SUB_IN: return 'matcheventtype_si'
    case MatchEventType.ET_SUB_OUT: return 'matcheventtype_so'
    case MatchEventType.ET_SHOT_ON_GOAL: return 'matcheventtype_sog'
    case MatchEventType.ET_YELLOW_RED_CARD: return 'matcheventtype_y2c'
    case MatchEventType.ET_YELLOW_CARD: return 'matcheventtype_yc'
    case MatchEventType.ET_ADM_MATCH_STARTED: return 'admevent_matchstarted'
    case MatchEventType.ET_ADM_MATCH_FINISHED: return 'admevent_matchfinished'
    case MatchEventType.ET_ADM_HALF_TIME_STARTED: return 'admevent_halftimestarted'
    case MatchEventType.ET_ADM_SECOND_HALF_STARTED: return 'admevent_secondhalfstarted'
    case MatchEventType.ET_ADM_FULL_TIME: return 'admevent_fulltime'
    case MatchEventType.ET_ADM_EXTRA_HALF_1_STARTED: return 'admevent_extrahalf1started'
    case MatchEventType.ET_ADM_EXTRA_TIME_BREAK_STARTED: return 'admevent_extratimebreakstarted'
    case MatchEventType.ET_ADM_EXTRA_HALF_2_STARTED: return 'admevent_extrahalf2started'
    case MatchEventType.ET_ADM_WAITING_FOR_PENALTY_SHOOTOUT: return 'admevent_waitingforpenaltyshootout'
    case MatchEventType.ET_ADM_PENALTY_SHOOTOUT_STARTED: return 'admevent_penaltyshootoutstarted'
    case MatchEventType.ET_UNKNOWN: return ''
    default: throw error(`MatchEventType.${MatchEventType[type]}`)
  }
}

export const BonusScoreToken = (type: BonusScoreType) => {
  switch (type) {
    case BonusScoreType.BS_ACTIVITY_BONUS: return 'bonusscoretype_act'
    case BonusScoreType.BS_CORNER: return 'bonusscoretype_c'
    case BonusScoreType.BS_CLEAN_SHEET_BONUS: return 'bonusscoretype_cs'
    case BonusScoreType.BS_GOAL: return 'bonusscoretype_g'
    case BonusScoreType.BS_SHOT_ON_GOAL: return 'bonusscoretype_sog'
    case BonusScoreType.BS_TEAM_IN_DRAW_BONUS: return 'bonusscoretype_tid'
    case BonusScoreType.BS_TEAM_IN_LEAD_BONUS: return 'bonusscoretype_til'
    case BonusScoreType.BS_UNKNOWN: return ''
    default: throw error(`BonusScoreToken.${BonusScoreToken[type]}`)
  }
}

export const MatchPeriodToken = (type: MatchPeriod) => {
  switch (type) {
    case MatchPeriod.MP_EXTRA_TIME_1: return 'matchperiod_extratime1'
    case MatchPeriod.MP_EXTRA_TIME_2: return 'matchperiod_extratime2'
    case MatchPeriod.MP_EXTRA_TIME_BREAK: return 'matchperiod_extratimebreak'
    case MatchPeriod.MP_FIRST_HALF: return 'matchperiod_firsthalf'
    case MatchPeriod.MP_FULL_TIME: return 'matchperiod_fulltime'
    case MatchPeriod.MP_HALF_TIME: return 'matchperiod_halftime'
    case MatchPeriod.MP_PENALTY_SHOOTOUT: return 'matchperiod_penaltyshootout'
    case MatchPeriod.MP_SECOND_HALF: return 'matchperiod_secondhalf'
    case MatchPeriod.MP_UNKNOWN: return 'matchperiod_unknown'
    default: throw error(`MatchPeriod.${MatchPeriod[type]}`)
  }
}

export const ContestGoalTypeToken = (type: ContestGoal.Type) => {
  switch (type) {
    case ContestGoal.Type.CGT_TOP_SCORE: return 'contest_goal_topscore'
    case ContestGoal.Type.CGT_TOTAL_GAMES: return 'contest_goal_totalgames'
    case ContestGoal.Type.CGT_TOTAL_SCORE_BEST_X: return 'contest_goal_bestmatchscore'
    case ContestGoal.Type.CGT_TOTAL_WINNINGS: return 'contest_goal_totalwinnings'
    case ContestGoal.Type.CGT_TOTAL_WINS: return 'contest_goal_totalwins'
    default: throw error(`ContestGoal.Type.${ContestGoal.Type[type]}`)
  }
}

export const ContestGoalTypeExplanationToken = (type: ContestGoal.Type) => {
  switch (type) {
    case ContestGoal.Type.CGT_TOP_SCORE: return 'contest_goal_topscore_explanation'
    case ContestGoal.Type.CGT_TOTAL_GAMES: return 'contest_goal_totalgames_explanation'
    case ContestGoal.Type.CGT_TOTAL_SCORE_BEST_X: return 'contest_goal_bestmatchscore_explanation'
    case ContestGoal.Type.CGT_TOTAL_WINNINGS: return 'contest_goal_totalwinnings_explanation'
    case ContestGoal.Type.CGT_TOTAL_WINS: return 'contest_goal_totalwins_explanation'
    default: throw error(`ContestGoal.Type.Explanation.${ContestGoal.Type[type]}`)
  }
}

export const MatchStatusToken = (status: MatchStatus) => {
  switch (status) {
    case MatchStatus.MS_LIVE: return 'matchstatus_live'
    case MatchStatus.MS_UNKNOWN: // TODO: Missing translation?
    case MatchStatus.MS_UPCOMING: return 'matchstatus_upcoming'
    case MatchStatus.MS_FINISHED: return 'matchstatus_finished'
    case MatchStatus.MS_CANCELLED: return 'matchstatus_cancelled'
    default: throw error(`MatchStatus.${MatchStatus[status]}`)
  }
}

export const CreateGameErrorToken = (result: CreateGameResponse.Result) => {
  switch (result) {
    case CreateGameResponse.Result.CGR_EXPIRED_MATCH: return 'gamesetup_error_matchexpired_message'
    case CreateGameResponse.Result.CGR_INVALID_ENTRY_FEE: return 'gamesetup_error_insufficientbalance_message'
    case CreateGameResponse.Result.CGR_INSUFFICIENT_AUTOSUBS: return 'gamesetup_error_insufficientautosubs_message'
    case CreateGameResponse.Result.CGR_UNKNOWN_ERROR: return 'common_errors_message_unexpected'
    case CreateGameResponse.Result.CGR_INSUFFICIENT_BALANCE: return 'gamesetup_error_insufficientbalance_message'
    case CreateGameResponse.Result.CGR_INVALID_TYPE: return 'common_errors_message_unexpected'
    case CreateGameResponse.Result.CGR_INVALID_MATCH: return 'common_errors_message_unexpected'
    case CreateGameResponse.Result.CGR_INVITATION_LIMIT_EXCEEDED: return 'common_errors_message_unexpected'
    case CreateGameResponse.Result.CGR_INVALID_PLAYER_ID: return 'common_errors_message_unexpected'
    case CreateGameResponse.Result.CGR_INSUFFICIENT_BUDGET: return 'teameditor_errors_message_overbudget'
    case CreateGameResponse.Result.CGR_SAVE_UNAVAILABLE: return 'teameditor_errors_message_saveunavailable'
    case CreateGameResponse.Result.CGR_GOALKEEPER_LIMIT_EXCEEDED: return 'teameditor_error_overgoalkeeperlimit'
    default: throw error(`CreateGameResponse.Result.${CreateGameResponse.Result[result]}`)
  }
}

export const PlayerStatusToken = (status: PlayerStatus) => {
  switch (status) {
    case PlayerStatus.PS_UNKNOWN: return 'playerstatus_unknown'
    case PlayerStatus.PS_ACTIVE: return 'playerstatus_active'
    case PlayerStatus.PS_INACTIVE: return 'playerstatus_inactive'
    case PlayerStatus.PS_LEFTOUT: return 'playerstatus_leftout'
    case PlayerStatus.PS_SENTOFF: return 'playerstatus_sentoff'
    case PlayerStatus.PS_FINISHED: return 'playerstatus_finished'
    default: throw error(`PlayerStatus.${PlayerStatus[status]}`)
  }
}
try {
    // @ts-ignore
    AchievementTypeToken.displayName = "AchievementTypeToken";
    // @ts-ignore
    AchievementTypeToken.__docgenInfo = { "description": "", "displayName": "AchievementTypeToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#AchievementTypeToken"] = { docgenInfo: AchievementTypeToken.__docgenInfo, name: "AchievementTypeToken", path: "src/lib/enumTranslations.ts#AchievementTypeToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    WonReferralBonusTypeToken.displayName = "WonReferralBonusTypeToken";
    // @ts-ignore
    WonReferralBonusTypeToken.__docgenInfo = { "description": "", "displayName": "WonReferralBonusTypeToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#WonReferralBonusTypeToken"] = { docgenInfo: WonReferralBonusTypeToken.__docgenInfo, name: "WonReferralBonusTypeToken", path: "src/lib/enumTranslations.ts#WonReferralBonusTypeToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    PossibleReferralBonusTypeToken.displayName = "PossibleReferralBonusTypeToken";
    // @ts-ignore
    PossibleReferralBonusTypeToken.__docgenInfo = { "description": "", "displayName": "PossibleReferralBonusTypeToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#PossibleReferralBonusTypeToken"] = { docgenInfo: PossibleReferralBonusTypeToken.__docgenInfo, name: "PossibleReferralBonusTypeToken", path: "src/lib/enumTranslations.ts#PossibleReferralBonusTypeToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    GameOutcomeToken.displayName = "GameOutcomeToken";
    // @ts-ignore
    GameOutcomeToken.__docgenInfo = { "description": "", "displayName": "GameOutcomeToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#GameOutcomeToken"] = { docgenInfo: GameOutcomeToken.__docgenInfo, name: "GameOutcomeToken", path: "src/lib/enumTranslations.ts#GameOutcomeToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    PlayerPositionNameToken.displayName = "PlayerPositionNameToken";
    // @ts-ignore
    PlayerPositionNameToken.__docgenInfo = { "description": "", "displayName": "PlayerPositionNameToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#PlayerPositionNameToken"] = { docgenInfo: PlayerPositionNameToken.__docgenInfo, name: "PlayerPositionNameToken", path: "src/lib/enumTranslations.ts#PlayerPositionNameToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    PlayerPositionCodeToken.displayName = "PlayerPositionCodeToken";
    // @ts-ignore
    PlayerPositionCodeToken.__docgenInfo = { "description": "", "displayName": "PlayerPositionCodeToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#PlayerPositionCodeToken"] = { docgenInfo: PlayerPositionCodeToken.__docgenInfo, name: "PlayerPositionCodeToken", path: "src/lib/enumTranslations.ts#PlayerPositionCodeToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    MatchEventToken.displayName = "MatchEventToken";
    // @ts-ignore
    MatchEventToken.__docgenInfo = { "description": "", "displayName": "MatchEventToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#MatchEventToken"] = { docgenInfo: MatchEventToken.__docgenInfo, name: "MatchEventToken", path: "src/lib/enumTranslations.ts#MatchEventToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    BonusScoreToken.displayName = "BonusScoreToken";
    // @ts-ignore
    BonusScoreToken.__docgenInfo = { "description": "", "displayName": "BonusScoreToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#BonusScoreToken"] = { docgenInfo: BonusScoreToken.__docgenInfo, name: "BonusScoreToken", path: "src/lib/enumTranslations.ts#BonusScoreToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    MatchPeriodToken.displayName = "MatchPeriodToken";
    // @ts-ignore
    MatchPeriodToken.__docgenInfo = { "description": "", "displayName": "MatchPeriodToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#MatchPeriodToken"] = { docgenInfo: MatchPeriodToken.__docgenInfo, name: "MatchPeriodToken", path: "src/lib/enumTranslations.ts#MatchPeriodToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    ContestGoalTypeToken.displayName = "ContestGoalTypeToken";
    // @ts-ignore
    ContestGoalTypeToken.__docgenInfo = { "description": "", "displayName": "ContestGoalTypeToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#ContestGoalTypeToken"] = { docgenInfo: ContestGoalTypeToken.__docgenInfo, name: "ContestGoalTypeToken", path: "src/lib/enumTranslations.ts#ContestGoalTypeToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    ContestGoalTypeExplanationToken.displayName = "ContestGoalTypeExplanationToken";
    // @ts-ignore
    ContestGoalTypeExplanationToken.__docgenInfo = { "description": "", "displayName": "ContestGoalTypeExplanationToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#ContestGoalTypeExplanationToken"] = { docgenInfo: ContestGoalTypeExplanationToken.__docgenInfo, name: "ContestGoalTypeExplanationToken", path: "src/lib/enumTranslations.ts#ContestGoalTypeExplanationToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    MatchStatusToken.displayName = "MatchStatusToken";
    // @ts-ignore
    MatchStatusToken.__docgenInfo = { "description": "", "displayName": "MatchStatusToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#MatchStatusToken"] = { docgenInfo: MatchStatusToken.__docgenInfo, name: "MatchStatusToken", path: "src/lib/enumTranslations.ts#MatchStatusToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    CreateGameErrorToken.displayName = "CreateGameErrorToken";
    // @ts-ignore
    CreateGameErrorToken.__docgenInfo = { "description": "", "displayName": "CreateGameErrorToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#CreateGameErrorToken"] = { docgenInfo: CreateGameErrorToken.__docgenInfo, name: "CreateGameErrorToken", path: "src/lib/enumTranslations.ts#CreateGameErrorToken" };
}
catch (__react_docgen_typescript_loader_error) { }try {
    // @ts-ignore
    PlayerStatusToken.displayName = "PlayerStatusToken";
    // @ts-ignore
    PlayerStatusToken.__docgenInfo = { "description": "", "displayName": "PlayerStatusToken", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/lib/enumTranslations.ts#PlayerStatusToken"] = { docgenInfo: PlayerStatusToken.__docgenInfo, name: "PlayerStatusToken", path: "src/lib/enumTranslations.ts#PlayerStatusToken" };
}
catch (__react_docgen_typescript_loader_error) { }